import * as joint from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';

import { FlowchartElementType, SelectionBehavior } from '@treetop/shared/model';

import { ContainerElementClasses, ElementClasses } from '../../model/element-classes.model';
import { FlowchartConfig, FontAttributes } from '../base/flowchart.config';
import { HeaderElement } from '../shapes/shared-header';
import { ContainerAttributes } from './container/container-attributes';
import { ContainerResize } from './container/ContainerResize';
import { LinkAnchorData } from './operations.service';
import { PortsService } from './ports.service';
import { PositionService } from './position.service';

export type CreateContainerNodeOptions = {
  x: number;
  y: number;
  allowEvents: boolean;
  size: { width: number; height: number };
  id: string;
  uniqueId: string;
  colorPresetId: string;
};

export type CreateContainerHeaderOptions = {
  text: string;
  x: number;
  y: number;
  id: string;
  colorPresetId: string;
};

export type CreateContainerSubComponentOptions = {
  text: string;
  size: number;
  type: FlowchartElementType;
  id: string;
  uniqueId: string;
  allowEvents: boolean;
  colorPresetId: string;
};

export class ContainerNodeService {
  public static createContainer(paper: joint.dia.Paper, options?: Partial<CreateContainerNodeOptions>): joint.dia.Cell {
    const container = new joint.shapes.standard.Rectangle({
      position: { x: options?.x, y: options?.y },
      size: {
        width: options?.size ? options?.size.width : FlowchartConfig.containerNode.size.width,
        height: options?.size ? options?.size.height : FlowchartConfig.containerNode.size.height,
      },
      z: 1,
      attrs: {
        body: {
          class: ContainerElementClasses.ContainerBody,
          rx: FlowchartConfig.containerNode.styles.borderRadius,
          ry: FlowchartConfig.containerNode.styles.borderRadius,
          strokeDasharray: '0 4 0',
          uniqueId: options?.uniqueId ? options?.uniqueId : '',
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-fill) !important` : undefined,
          stroke: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-stroke) !important` : undefined,
          'data-testid': `container-node_body-${options?.uniqueId}`,
        },
      },
    });

    container.on('change:size', (_element: joint.dia.Element, _newSize: joint.dia.Size) => {
      ContainerResize.adjustContainerSizeOnResize(_element, _newSize, paper);
    });

    container.on('change:embeds', (_element: joint.dia.Element, _embeds: string[]) => {
      if (ContainerResize.isAllSubActionsRemoved(_element)) {
        _element.set(ContainerAttributes.HasSubAction, false);
      }
      ContainerResize.adjustContainerSizeOnEmbedChange(_element, paper);
      ContainerResize.repositionContainerElements(_element, paper, true);
    });

    if (options?.id) {
      container.set('id', options?.id);
    }
    container.attr('body/unique-id', options?.uniqueId ?? container.id);

    container.set('isContainer', true);

    PortsService.addPorts(container);
    return container;
  }

  public static createContainerHeader(paper: joint.dia.Paper, options?: Partial<CreateContainerHeaderOptions>): joint.dia.Cell {
    const containerConfiguration = FlowchartConfig.containerNode;

    const containerHeader = new HeaderElement(
      {
        position: {
          x: options?.x + containerConfiguration.styles.headerNegativePadding,
          y: options?.y + containerConfiguration.styles.headerNegativePadding,
        },
        size: containerConfiguration.headerSize,
        attrs: {
          body: {
            class: `${ContainerElementClasses.ContainerHeaderBody} ${ElementClasses.ElementBody}`,
            fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-fill) !important` : undefined,
            stroke: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-stroke) !important` : undefined,
            rx: containerConfiguration.styles.headerBorderRadius,
            ry: containerConfiguration.styles.headerBorderRadius,
          },
          label: {
            class: `${ContainerElementClasses.ContainerHeaderText} ${ElementClasses.ElementText}`,
            fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-text) !important` : undefined,
            ...FontAttributes,
            fontSize: FontAttributes.fontSize,
            text: options?.text,
            textVerticalAnchor: 'bottom',
            transform: 'translate(0px, 8px)',
          },
        },
      },
      paper,
    );

    if (options?.id) {
      containerHeader.set('id', options?.id);
    }

    return containerHeader;
  }

  public static createSubContainerArrow(
    icon: string,
    type: ContainerAttributes.IsSubComponentUpArrow | ContainerAttributes.IsSubComponentDownArrow,
  ): joint.dia.Cell {
    const CustomRectangle = joint.dia.Element.define('custom.Rectangle', {
      attrs: {
        root: {
          'data-arrow-type': type,
        },
        body: {
          width: FlowchartConfig.containerNode.subComponentNode.arrow.width,
          height: FlowchartConfig.containerNode.subComponentNode.arrow.height,
          class: 'element--sub-container-arrow__icon',
        },
        svgBackground: {
          'xlink:href': icon,
          width: FlowchartConfig.containerNode.subComponentNode.arrow.width,
          height: FlowchartConfig.containerNode.subComponentNode.arrow.height,
          class: 'element--sub-container-arrow__icon',
          x: 0,
          y: 0,
        },
      },
      markup: [
        {
          tagName: 'image',
          selector: 'svgBackground',
        },
        {
          tagName: 'rect',
          selector: 'body',
        },
      ],
    });

    const subContainerArrow = new CustomRectangle();

    subContainerArrow.set(ContainerAttributes.IsSubComponentArrow, true);
    subContainerArrow.set(type, true);
    subContainerArrow.size(
      FlowchartConfig.containerNode.subComponentNode.arrow.width,
      FlowchartConfig.containerNode.subComponentNode.arrow.height,
    );

    return subContainerArrow;
  }

  public static createContainerSubComponent(
    paper: joint.dia.Paper,
    graph: joint.dia.Graph,
    options?: Partial<CreateContainerSubComponentOptions>,
  ): joint.dia.Cell {
    const { subComponentNode, styles } = FlowchartConfig.containerNode;
    const bodyClass =
      options?.type === FlowchartElementType.FlowchartAction
        ? ContainerElementClasses.ContainerSubactionBody
        : ContainerElementClasses.ContainerSubdecisionBody;
    const textClass =
      options?.type === FlowchartElementType.FlowchartAction
        ? ContainerElementClasses.ContainerSubactionText
        : ContainerElementClasses.ContainerSubdecisionText;

    const containerSubComponent = new joint.shapes.standard.Rectangle({
      type: 'custom.ContainerSubComponent',
      size: {
        width: options?.size - FlowchartConfig.containerNode.subComponentNode.offset,
        height: subComponentNode.size.height,
      },
      z: 2,
      attrs: {
        body: {
          class: `${bodyClass} ${ElementClasses.ElementBody}`,
          rx: styles.borderRadius,
          ry: styles.borderRadius,
          uniqueId: options?.uniqueId ? options?.uniqueId : '',
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-fill) !important` : undefined,
          stroke: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-stroke) !important` : undefined,
        },
        label: {
          class: `${ContainerElementClasses.ContainerSubcomponentText} ${textClass} ${ElementClasses.ElementText}`,
          fill: options?.colorPresetId ? `var(--tt-color-preset-${options?.colorPresetId}-text) !important` : undefined,
          ...FontAttributes,
          fontSize: FontAttributes.fontSize,
          text: options?.text,
          textWrap: {
            maxLineCount: 2,
            width: options?.allowEvents ? -40 : -10,
            ellipsis: true,
          },
          textVerticalAnchor: 'middle',
          refX: options?.allowEvents ? '8' : '50%',
          textAnchor: options?.allowEvents ? 'start' : 'middle',
        },
      },
    });

    if (options?.type === FlowchartElementType.FlowchartAction) {
      containerSubComponent.set('isContainerSubAction', true);
    } else {
      containerSubComponent.set('isContainerSubDecision', true);
    }
    containerSubComponent.set('isContainerSubComponent', true);

    containerSubComponent.set('id', options?.id ?? containerSubComponent.id);
    containerSubComponent.attr('body/data-testid', `action-node_body-${options?.id ?? containerSubComponent.id}`);
    containerSubComponent.attr('body/unique-id', options?.uniqueId ?? containerSubComponent.id);

    if (options?.allowEvents) {
      const arrowUp = ContainerNodeService.createSubContainerArrow(
        'assets/icons/flowchart-arrow-up.svg',
        ContainerAttributes.IsSubComponentUpArrow,
      );

      const arrowDown = ContainerNodeService.createSubContainerArrow(
        'assets/icons/flowchart-arrow-down.svg',
        ContainerAttributes.IsSubComponentDownArrow,
      );

      graph.addCell(arrowUp);
      containerSubComponent.embed(arrowUp);
      graph.addCell(arrowDown);
      containerSubComponent.embed(arrowDown);

      const arrowUpView = paper.findViewByModel(arrowUp);
      arrowUpView.$el.hide();
      const arrowDownView = paper.findViewByModel(arrowDown);
      arrowDownView.$el.hide();
    }

    containerSubComponent.on('change:parent', (element: joint.dia.Cell, parentId: string) => {
      if (parentId) {
        graph.getCell(parentId).set(ContainerAttributes.HasSubAction, true);
      }
    });

    return containerSubComponent;
  }

  static createContainerSelectionBehavior(
    selectionBehavior: SelectionBehavior,
    translocoService: TranslocoService,
    x: number,
    y: number,
    height: number,
    width: number,
  ): joint.dia.Cell {
    x = x + width / 2;
    y = y + height;
    const text = translocoService.translate(SelectionBehavior[selectionBehavior]);
    const step = new joint.shapes.standard.Rectangle({
      position: { x, y },
      z: 1,
      size: FlowchartConfig.selectionBehaviorContainer.size,
      attrs: {
        body: {
          class: `${ContainerElementClasses.ContainerSelectionBehaviourBody} ${ElementClasses.ElementBody}`,
        },
        label: {
          class: `${ContainerElementClasses.ContainerSelectionBehaviourText} ${ElementClasses.ElementText}`,
          fill: '#063d70',
          ...FontAttributes,
          fontSize: FontAttributes.fontSize,
          textVerticalAnchor: 'middle',
          text,
        },
      },
    });

    step.set('isContainerSelectionBehavior', true);

    return step;
  }

  static createContainerInDefaultPosition(
    paper: joint.dia.Paper,
    sourceElement: joint.dia.ElementView,
    sourceAnchor: LinkAnchorData,
    positionService: PositionService,
    overwrittenPosition?: joint.g.PlainPoint,
  ): joint.dia.Cell {
    if (overwrittenPosition) {
      return ContainerNodeService.createContainer(paper, { x: overwrittenPosition.x, y: overwrittenPosition.y });
    }

    const containerPosition = positionService.calculateStepPosition(
      sourceElement.model.attributes.size,
      sourceElement.model.attributes.position,
      sourceAnchor,
      FlowchartConfig.containerNode.size,
    );

    return ContainerNodeService.createContainer(paper, { x: containerPosition.x, y: containerPosition.y });
  }
}
